import { call, put, delay } from 'redux-saga/effects';
import processError from '../../../utils/processError';
import { loadVehiclesListBegin, loadVehiclesListFailed, loadVehiclesListSuccessful } from '../actions';
import { resolveActionPromise } from '../../../utils/resolveActionPromise';
import makeRequest, { getBodyJson } from '../../../utils/makeRequest';
import { rejectActionPromise } from '../../../utils/rejectActionPromise';
import { OK } from '../../../configs/serverStatusCodes';
import {
  BACKEND_MDS_URL, BACKEND_URL, DEFAULT_ITEMS_OF_PAGE, LOADING_DELAY,
} from '../../../configs';
import addFilterQueryWithCondition from '../../../utils/addFilterQueryWithCondition';
import { projectDateToTimestamp } from '../../../utils/projectTime';

const getPaginationFromLink = link => {
  if (!link) {
    return {};
  }
  try {
    const baseUrl = typeof window !== 'undefined' && window.location
      ? window.location.origin
      : 'http://localhost';
    const url = new URL(link, baseUrl);
    return {
      page: Number(url.searchParams.get('page[number]')) || undefined,
      limit: Number(url.searchParams.get('page[size]')) || undefined,
    };
  } catch (e) {
    return {};
  }
};

const getVehiclesMeta = (responseData = {}, page, limit) => {
  if (responseData.meta) {
    return responseData.meta;
  }

  const links = responseData.links || {};
  const lastPage = getPaginationFromLink(links.last).page;
  const currentPage = Number(page) || 1;
  const currentLimit = Number(limit) || DEFAULT_ITEMS_OF_PAGE;
  const currentLength = Array.isArray(responseData.data) ? responseData.data.length : 0;
  const fallbackTotal = lastPage
    ? (currentPage === lastPage
      ? ((lastPage - 1) * currentLimit) + currentLength
      : lastPage * currentLimit)
    : currentLength;

  return {
    limit: currentLimit,
    page: currentPage,
    total: fallbackTotal,
    pages: lastPage || (fallbackTotal ? Math.ceil(fallbackTotal / currentLimit) : 0),
  };
};

export default function* loadVehiclesList(action) {
  try {
    const { payload = {} } = action;
    const {
      link = null,
      limit = DEFAULT_ITEMS_OF_PAGE,
      page = 1,
      filter,
    } = payload;
    const linkPagination = getPaginationFromLink(link);
    const requestPage = linkPagination.page || page;
    const requestLimit = linkPagination.limit || limit;
    let pool = link ? {} : {
      'page[number]': requestPage,
      'page[size]': requestLimit,
    };
    const normalizedFilter = typeof filter === 'object' && filter ? { ...filter } : filter;
    if (normalizedFilter?.updated_from) {
      normalizedFilter.updated_from = projectDateToTimestamp(normalizedFilter.updated_from);
    }
    if (normalizedFilter?.updated_to) {
      normalizedFilter.updated_to = projectDateToTimestamp(normalizedFilter.updated_to, true);
    }
    if (!link && typeof normalizedFilter === 'object' && Object.keys(normalizedFilter).length) {
      yield delay(LOADING_DELAY);
      pool = addFilterQueryWithCondition(normalizedFilter, pool);
    }
    const url = link || `${BACKEND_MDS_URL}/agency/vehicles/all`;
    yield put(loadVehiclesListBegin());
    const dataResponse = yield makeRequest(url, pool, action, 'GET', true);
    const data = yield getBodyJson(dataResponse);
    if (dataResponse.status === OK && data?.data) {
      // enrich vehicles with provider names
      try {
        const providersResp = yield makeRequest(`${BACKEND_URL}/providers`, {}, action, 'GET', true);
        const providersBody = yield getBodyJson(providersResp);
        let providers = [];
        if (providersResp.status === OK && providersBody?.data) {
          providers = providersBody.data;
        }
        const providerMap = providers.reduce((m, p) => ({ ...m, [p.id]: p.name }), {});
        const enriched = {
          ...data,
          meta: getVehiclesMeta(data, requestPage, requestLimit),
          data: (data.data || []).map(v => ({ ...v, provider_name: providerMap[v.provider_id] || v.provider_id })),
        };
        yield put(loadVehiclesListSuccessful(enriched));
        yield call(resolveActionPromise(action, enriched));
      } catch (e) {
        // fallback to original data if providers fetch fails
        const normalizedData = {
          ...data,
          meta: getVehiclesMeta(data, requestPage, requestLimit),
        };
        yield put(loadVehiclesListSuccessful(normalizedData));
        yield call(resolveActionPromise(action, normalizedData));
      }
    } else {
      yield put(loadVehiclesListFailed());
      yield call(rejectActionPromise(action, data.data));
    }
  } catch (e) {
    yield put(loadVehiclesListFailed());
    yield call(rejectActionPromise(action, e));
    yield processError(e);
  } finally {
    yield call(resolveActionPromise(action));
  }
}
